import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';
import { GestureDetector } from 'react-native-gesture-handler';
import * as ImageManipulator from 'expo-image-manipulator';
import { ImagePickerAsset } from 'expo-image-picker';

import { spacing } from '../../../theme';
import { Text } from '../../Text';
import { IconButton } from '../../IconButton';
import { Slider } from '../../Slider';
import { Modal, ModalActions } from '../../Modal';
import {
	AVATAR_CIRCLE_DIAMETER,
	CropPhotoParams,
	useCropProfilePhoto,
} from './useCropProfilePhoto';
import { AddPlusIcon, RemoveMinusIcon } from '../../../assets';

interface Props extends CropPhotoParams {
	imageToCrop: ImagePickerAsset | null;
	onSave: (croppedImage: ImageManipulator.ImageResult) => void;
	onClose: () => void;
}

export const CropProfilePhotoModal = ({ imageToCrop, onSave, onClose }: Props) => {
	const { t } = useTranslation();
	const {
		cropImage,
		animatedStyles,
		scaleValue,
		handleScaleChange,
		minScale,
		maxScale,
		panGesture,
		scaleStep,
		stepUpScale,
		stepDownScale,
		scalingDisabled,
	} = useCropProfilePhoto({ imageToCrop, onSave });

	if (!imageToCrop) return null;

	return (
		<Modal
			visible={Boolean(imageToCrop)}
			onClose={onClose}
			title={t('crop-photo-modal.title')}
			actions={
				<ModalActions
					primaryAction={{
						label: t('common.save'),
						onPress: cropImage,
					}}
					secondaryAction={{
						label: t('common.cancel'),
						onPress: onClose,
					}}
				/>
			}
		>
			<View style={styles.container}>
				<View style={styles.front}>
					<Animated.Image source={imageToCrop} style={animatedStyles} />
				</View>
				<GestureDetector gesture={panGesture}>
					<Animated.Image source={imageToCrop} style={[styles.back, animatedStyles]} />
				</GestureDetector>
			</View>
			<View style={styles.sliderContainer}>
				<IconButton
					size="small"
					appearance="ghost"
					Icon={RemoveMinusIcon}
					onPress={stepDownScale}
					disabled={scalingDisabled}
				/>
				<Slider
					minimumValue={minScale}
					maximumValue={maxScale}
					onValueChange={handleScaleChange}
					value={scaleValue}
					step={scaleStep}
					disabled={scalingDisabled}
					style={styles.slider}
				/>
				<IconButton
					size="small"
					appearance="ghost"
					Icon={AddPlusIcon}
					onPress={stepUpScale}
					disabled={scalingDisabled}
				/>
			</View>

			<Text category="p2">{t('crop-photo-modal.move-and-scale-the-photo')}</Text>
		</Modal>
	);
};

const styles = StyleSheet.create({
	container: {
		height: 300,
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		cursor: 'grab',
	},
	front: {
		height: AVATAR_CIRCLE_DIAMETER,
		width: AVATAR_CIRCLE_DIAMETER,
		borderRadius: AVATAR_CIRCLE_DIAMETER / 2,
		position: 'absolute',
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'center',
	},
	back: {
		opacity: 0.5,
	},
	sliderContainer: {
		marginTop: spacing(4),
		flexDirection: 'row',
		gap: spacing(),
	},
	slider: {
		flex: 1,
	},
});
